/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ApiError, UpdateSubtenantMembershipData, UpdateSubtenantMembershipResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UpdateUserMutationConfig = {
  onSuccess?: (data: UpdateSubtenantMembershipResponse, variables: UpdateSubtenantMembershipData) => void;
  onError?: (error: unknown, variables: UpdateSubtenantMembershipData) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: UpdateSubtenantMembershipData) => void;
};

/**
 * Hook for updating subtenant membership.
 *
 * @param config.onSuccess - Called after successful user update
 * @param config.onError - Called if update fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for updating subtenant membership
 *
 * @example
 * ```tsx
 * const { mutate } = useUpdateSubtenantMembership({
 *   onSuccess: (data) => console.log('SubtenantMembership updated:', data),
 *   onError: (error) => console.error('SubtenantMembership update failed:', error)
 * });
 *
 * // Update subtenant membership
 * mutate({ userId: '9fb763f7-33de-4969-a7a0-b808f4637318', role: 'admin' });
 * ```
 */

export function useUpdateSubtenantMembership({ onSuccess, onError, onSettled, onMutate }: UpdateUserMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isError, error } = useMutation<
    UpdateSubtenantMembershipResponse,
    ApiError,
    UpdateSubtenantMembershipData
  >({
    mutationFn: ({ id, userId, requestBody }) => {
      return mgmtPlaneAPIClient.subtenants.updateSubtenantMembership({
        id,
        userId,
        requestBody,
        tenantId: tenantId!,
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error };
}
